import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, Button, Popconfirm, Tooltip, message } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getUserDetails, makePostCall } from '../utils/helper';
import { FieldLabels } from '../constants/fieldLabels';

/**
 * "Deleted Studies" dialog (profile dropdown) — lists soft-deleted orders with
 * minimal columns and a single Restore action. Restoring re-activates the order
 * and revokes the dcm4chee rejection so the study reappears everywhere.
 */
const DeletedStudiesModal = ({ visible, onCancel }: { visible: boolean; onCancel: () => void }) => {
  const [list, setList] = useState({ data: [], loading: true, total: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const fetchDeleted = useCallback((page = 1, pageSize = 20) => {
    setList(prev => ({ ...prev, loading: true }));
    makePostCall('/deleted-orders', { page: page - 1, limit: pageSize })
      .then(res => {
        setList({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        message.error(e?.response?.data?.message || e.message || 'Failed to load deleted studies');
        setList({ data: [], loading: false, total: 0 });
      });
  }, []);

  useEffect(() => {
    if (visible) {
      fetchDeleted(pagination.current, pagination.pageSize);
    }
  }, [visible]);

  const handleRestore = (record) => {
    setRestoringId(record.pacs_ord_id);
    makePostCall('/restore-order', {
      order_id: record.pacs_ord_id,
      user_id: getUserDetails()?.username,
    })
      .then(() => {
        message.success('Study restored');
        fetchDeleted(pagination.current, pagination.pageSize);
      })
      .catch(e => {
        message.error(e?.response?.data?.message || e.message || 'Failed to restore study');
      })
      .finally(() => setRestoringId(null));
  };

  const ellipsisCell = (text) => (
    <Tooltip title={text}>
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text || '-'}
      </div>
    </Tooltip>
  );

  const columns = [
    {
      title: FieldLabels.PATIENT_NAME,
      key: 'pat_name',
      width: 180,
      render: (_, rec) => ellipsisCell(rec?.patient?.pat_name),
    },
    {
      title: FieldLabels.PATIENT_ID,
      key: 'pat_pin',
      width: 110,
      render: (_, rec) => rec?.patient?.pat_pin,
    },
    {
      title: FieldLabels.ACCESSION_NUMBER_SHORT,
      dataIndex: 'po_acc_no',
      key: 'po_acc_no',
      width: 120,
      render: ellipsisCell,
    },
    {
      title: FieldLabels.MODALITY,
      dataIndex: 'po_modality',
      key: 'po_modality',
      width: 90,
    },
    {
      title: FieldLabels.STUDY_DESCRIPTION_SHORT,
      dataIndex: 'po_diag_desc',
      key: 'po_diag_desc',
      width: 200,
      render: ellipsisCell,
    },
    {
      title: 'Deleted By',
      dataIndex: 'po_deleted_by',
      key: 'po_deleted_by',
      width: 120,
      render: ellipsisCell,
    },
    {
      title: 'Deleted At',
      dataIndex: 'po_deleted_at',
      key: 'po_deleted_at',
      width: 150,
      render: val => (val ? dayjs(val).format('DD MMM YYYY, hh:mmA') : '-'),
    },
    {
      title: 'Reason',
      dataIndex: 'po_delete_reason',
      key: 'po_delete_reason',
      width: 160,
      render: ellipsisCell,
    },
    {
      title: FieldLabels.ACTIONS,
      key: 'actions',
      width: 100,
      render: (_, rec) => (
        <Popconfirm
          title="Restore this study?"
          okText="Restore"
          cancelText="Cancel"
          onConfirm={() => handleRestore(rec)}
        >
          <Button
            size="small"
            icon={<UndoOutlined />}
            loading={restoringId === rec.pacs_ord_id}
          >
            Restore
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="Deleted Studies"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1100}
    >
      <Table
        rowKey="pacs_ord_id"
        size="small"
        loading={list.loading}
        columns={columns}
        dataSource={list.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: list.total,
          showSizeChanger: false,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            fetchDeleted(page, pageSize);
          },
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
          position: ['bottomCenter'],
        }}
        scroll={{ x: 1000, y: '55vh' }}
      />
    </Modal>
  );
};

export default DeletedStudiesModal;
